/**
 * HUD FAIR MARKET RENT (FMR) DATA - BY ZIP CODE
 *
 * Source: HUD User FMR Documentation System
 * https://www.huduser.gov/portal/datasets/fmr.html
 *
 * FMR represents the 40th percentile of gross rents for typical,
 * non-substandard rental units. Used as baseline for Section 8
 * Housing Choice Voucher program.
 *
 * Philadelphia-Camden-Wilmington, PA-NJ-DE-MD MSA
 * FMR varies by ZIP code within the metro area
 *
 * UPDATE SCHEDULE: HUD releases new FMRs each August, effective October 1
 * Next update: FY 2027 (October 1, 2026)
 */

// Per-zip-code FMR data (FY 2026)
// Source: HUD Small Area Fair Market Rents (SAFMRs)
// Philadelphia County uses SAFMRs - rates vary by zip code
// Data from ushousingdata.com FY2025 + ~4% adjustment for FY2026
// 15 supported zip codes
const fmrByZipCode = {
  // Center City / Downtown
  '19103': { 0: 2140, 1: 2360, 2: 2810, 3: 3380, 4: 3850 },  // Rittenhouse
  '19106': { 0: 2140, 1: 2360, 2: 2810, 3: 3380, 4: 3850 },  // Old City
  '19107': { 0: 2080, 1: 2300, 2: 2740, 3: 3300, 4: 3740 },  // Washington Square

  // University City
  '19104': { 0: 1310, 1: 1440, 2: 1720, 3: 2070, 4: 2350 },  // University City

  // Northwest Philadelphia
  '19119': { 0: 1280, 1: 1410, 2: 1680, 3: 2030, 4: 2310 },  // West Mount Airy
  '19144': { 0: 1250, 1: 1380, 2: 1640, 3: 1980, 4: 2250 },  // Germantown
  '19150': { 0: 1280, 1: 1420, 2: 1710, 3: 2070, 4: 2390 },  // East Mount Airy

  // North Philadelphia
  '19121': { 0: 1250, 1: 1370, 2: 1630, 3: 1970, 4: 2240 },  // Brewerytown

  // Fishtown / Northern Liberties
  '19123': { 0: 1870, 1: 2060, 2: 2450, 3: 2950, 4: 3360 },  // Northern Liberties
  '19125': { 0: 1770, 1: 1940, 2: 2320, 3: 2800, 4: 3170 },  // Fishtown

  // Fairmount / Art Museum
  '19130': { 0: 1980, 1: 2170, 2: 2590, 3: 3120, 4: 3550 },  // Fairmount

  // West Philadelphia
  '19131': { 0: 1390, 1: 1510, 2: 1800, 3: 2160, 4: 2410 },  // Overbrook (user-confirmed FY2026)
  '19151': { 0: 1120, 1: 1240, 2: 1480, 3: 1780, 4: 2030 },  // Overbrook Park

  // South Philadelphia
  '19146': { 0: 1780, 1: 1960, 2: 2330, 3: 2810, 4: 3190 },  // Graduate Hospital
  '19148': { 0: 1450, 1: 1600, 2: 1900, 3: 2290, 4: 2610 },  // Passyunk
};

// Metro-wide fallback (used when zip-specific data not available)
const metroFallback = {
  0: 1200,  // Studio / Efficiency
  1: 1310,  // 1 Bedroom
  2: 1570,  // 2 Bedroom
  3: 1980,  // 3 Bedroom
  4: 2120   // 4 Bedroom
};

export const hudFmr2026 = {
  metroArea: 'Philadelphia-Camden-Wilmington, PA-NJ-DE-MD MSA',
  fiscalYear: 2026,
  effectiveDate: '2025-10-01',
  source: 'HUD FMR Documentation System',
  byZipCode: fmrByZipCode,
  metroFallback: metroFallback,
  largeUnitMultiplier: 0.15
};

/**
 * Get HUD FMR for a specific zip code and bedroom count
 * @param {number} bedrooms - Number of bedrooms (0-4+)
 * @param {string} zipCode - Optional zip code for zip-specific FMR
 * @returns {number} FMR rent amount
 */
export function getHudFmr(bedrooms, zipCode = null) {
  const bedroomCount = parseInt(bedrooms);

  // Get the appropriate rent table (zip-specific or metro fallback)
  let rentTable;
  if (zipCode && hudFmr2026.byZipCode[zipCode]) {
    rentTable = hudFmr2026.byZipCode[zipCode];
  } else {
    rentTable = hudFmr2026.metroFallback;
  }

  // Direct lookup for 0-4 bedrooms
  if (bedroomCount <= 4) {
    return rentTable[bedroomCount] || rentTable[2];
  }

  // For 5+ bedrooms, calculate based on 4BR + 15% per additional BR
  const fourBrRent = rentTable[4];
  const extraBedrooms = bedroomCount - 4;
  const multiplier = 1 + (extraBedrooms * hudFmr2026.largeUnitMultiplier);

  return Math.round(fourBrRent * multiplier);
}

/**
 * Check if we have zip-specific FMR data
 * @param {string} zipCode - Zip code to check
 * @returns {boolean} True if zip-specific data exists
 */
export function hasZipSpecificFmr(zipCode) {
  return !!hudFmr2026.byZipCode[zipCode];
}

/**
 * Check if a price is below HUD FMR (may indicate underpricing)
 * @param {number} price - Proposed rent price
 * @param {number} bedrooms - Number of bedrooms
 * @param {string} zipCode - Optional zip code for zip-specific FMR
 * @returns {Object} { isBelow, fmr, difference, percentBelow }
 */
export function compareToFmr(price, bedrooms, zipCode = null) {
  const fmr = getHudFmr(bedrooms, zipCode);
  const difference = price - fmr;
  const percentDiff = ((difference / fmr) * 100).toFixed(1);

  return {
    isBelow: price < fmr,
    fmr,
    difference,
    percentDiff,
    pricePosition: price < fmr
      ? 'below HUD baseline'
      : price > fmr * 1.1
        ? 'above market'
        : 'at market'
  };
}

/**
 * Get FMR data with metadata for display
 * @param {number} bedrooms - Number of bedrooms
 * @returns {Object} FMR data with context
 */
export function getFmrWithContext(bedrooms) {
  return {
    amount: getHudFmr(bedrooms),
    fiscalYear: hudFmr2026.fiscalYear,
    effectiveDate: hudFmr2026.effectiveDate,
    metroArea: hudFmr2026.metroArea,
    description: 'HUD Fair Market Rent (40th percentile)'
  };
}

export default hudFmr2026;
